import { Injectable } from "@angular/core";
import { AssetClass, BlotterType, OpsExceptionType } from "shared/cubiq-model-multi-asset";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  CcyLookupService,
  CcyPairLookupService,
  ColumnFilterType,
  IndexType,
  OpsIntEventPrivatizer,
  OpsIntLookupService,
  ProfilePromiseActionService,
} from "..";

import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { BfxColDef, TableDefinition } from "./TableDefinition";
import { DIReasonHelper } from "./orders/DIReasonHelper";
import { TranslateService } from "@ngx-translate/core";
import { Optional } from "@angular/core";
import { TreeMultiMap } from "jstreemap";
import { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";

const DEFAULT_SORT: Array<{ colId: string; sort: string }> = [
  { colId: "connectionGroup", sort: "asc" },
  { colId: "connection", sort: "asc" },
  { colId: "session", sort: "asc" },
];
const DEFAULT_CELL_CLASS: string = "ag-cell-default";

@Injectable()
export class ConnectionMonitorConnectionsTableDefinitionFactory extends BaseTableDefinitionFactory {
  public static COLUMN_STATUS: string = "status";
  public static COLUMN_CONNECTION_GROUP: string = "connectionGroup";
  public static COLUMN_CONNECTION: string = "connection";
  public static COLUMN_NODE: string = "node";
  public static COLUMN_SESSION: string = "session";
  public static COLUMN_LAST_EVENT: string = "lastEvent";
  public static COLUMN_TIMESTAMP: string = "eventTime";

  protected localeString: string = "DD-MMM-YY HH:mm:ss";

  constructor(
    profileService: ProfilePromiseActionService,
    @Optional() protected _translate: TranslateService,
  ) {
    super(profileService);
  }

  public createDef(): TableDefinition {
    super.createDef();
    let td = new TableDefinition("eventId", "as_sort_eventId");

    let statusColumn: BfxColDef = this.initColdDef();
    statusColumn.field = ConnectionMonitorConnectionsTableDefinitionFactory.COLUMN_STATUS;
    statusColumn.pinned = "left";
    statusColumn.lockPosition = true;
    statusColumn.headerName = "Status";
    statusColumn.sortable = true;
    statusColumn.suppressMovable = true;
    statusColumn.suppressSizeToFit = true;
    statusColumn.resizable = false;
    statusColumn.suppressMenu = true;
    statusColumn.columnFilterType = ColumnFilterType.NONE;
    statusColumn.omitFromPicker = true;
    statusColumn.disableInPicker = true;
    statusColumn.suppressSizeToFit = true;
    statusColumn.minWidth = this.oneWs;
    statusColumn.width = this.fourWs;
    statusColumn.maxWidth = this.fourWs;
    statusColumn.headerClass = ["connection-monitor_status_column-header"];
    statusColumn.cellClass = ["blotter_center_align_col", "connection-monitor_status_column"];
    statusColumn.headerClass = "connection-monitor_status_header";
    statusColumn.valueGetter = (params: ValueGetterParams) => {
      return params.data.statusType;
    };
    statusColumn.tooltipValueGetter = (params: ValueGetterParams) => {
      if (CubiqTS.moment.isMoment(params?.data?.timestamp)) {
        return params.data.timestamp.format(this.localeString);
      } else if (params?.data?.timestamp instanceof Date) {
        return CubiqTS.moment(params?.data?.timestamp).format(this.localeString);
      }
    };

    statusColumn.cellRenderer = (params: any) => {
      return (
        '<span class="connection_monitor_status connection_monitor_status_' +
        params.value +
        '"> </span>'
      );
    };
    statusColumn.position = this.position.next();
    td.addColumn(statusColumn);

    let connectionGroup: BfxColDef = this.initColdDef();
    connectionGroup.field =
      ConnectionMonitorConnectionsTableDefinitionFactory.COLUMN_CONNECTION_GROUP;
    connectionGroup.sortingOrder = ["asc", "desc"];
    connectionGroup.headerName = "Connection Group";
    connectionGroup.headerCheckboxSelection = false;
    connectionGroup.minWidth = this.oneWs;
    connectionGroup.width = this.elevenWs;
    connectionGroup.disableInPicker = true;
    connectionGroup.resizable = true;
    connectionGroup.valueGetter = (params: ValueGetterParams) => {
      return params.data.connectionGroup;
    };
    connectionGroup.indexType = IndexType.String;
    connectionGroup.position = this.position.next();
    td.addColumn(connectionGroup);

    let connection: BfxColDef = this.initColdDef();
    connection.field = ConnectionMonitorConnectionsTableDefinitionFactory.COLUMN_CONNECTION;
    connection.sortingOrder = ["asc", "desc"];
    connection.headerName = "Connection";
    connection.headerCheckboxSelection = false;
    connection.minWidth = this.oneWs;
    connection.resizable = true;
    connection.width = this.thirteenWs;
    connection.valueGetter = (params: ValueGetterParams) => {
      return params.data.connection;
    };
    connection.indexType = IndexType.String;
    connection.position = this.position.next();
    td.addColumn(connection);

    let session: BfxColDef = this.initColdDef();
    session.field = ConnectionMonitorConnectionsTableDefinitionFactory.COLUMN_SESSION;
    session.sortingOrder = ["asc", "desc"];
    session.headerName = "Session";
    session.headerCheckboxSelection = false;
    session.minWidth = this.oneWs;
    session.width = this.thirteenWs;
    session.resizable = true;
    session.valueGetter = (params: ValueGetterParams) => {
      return params.data.session;
    };
    session.indexType = IndexType.String;
    session.position = this.position.next();
    td.addColumn(session);

    let node: BfxColDef = this.initColdDef();
    node.field = ConnectionMonitorConnectionsTableDefinitionFactory.COLUMN_NODE;
    node.sortingOrder = ["asc", "desc"];
    node.headerName = "Node";
    node.headerCheckboxSelection = false;
    node.minWidth = this.oneWs;
    node.width = this.tenWs;
    node.resizable = true;
    node.valueGetter = (params: ValueGetterParams) => {
      return params.data.node;
    };
    node.indexType = IndexType.String;
    node.position = this.position.next();
    td.addColumn(node);

    let lastEvent: BfxColDef = this.initColdDef();
    lastEvent.field = ConnectionMonitorConnectionsTableDefinitionFactory.COLUMN_LAST_EVENT;
    lastEvent.sortingOrder = ["asc", "desc"];
    lastEvent.headerName = "Last Event";
    lastEvent.headerCheckboxSelection = false;
    lastEvent.minWidth = this.oneWs;
    lastEvent.width = this.thirteenWs;
    lastEvent.resizable = true;
    lastEvent.valueGetter = (params: ValueGetterParams) => {
      return params.data.status;
    };
    lastEvent.indexType = IndexType.String;
    lastEvent.position = this.position.next();
    td.addColumn(lastEvent);

    let timeColumn: BfxColDef = this.initColdDef();
    timeColumn.field = ConnectionMonitorConnectionsTableDefinitionFactory.COLUMN_TIMESTAMP;
    timeColumn.sortingOrder = ["asc", "desc"];
    timeColumn.headerName = "Event Time";
    timeColumn.headerCheckboxSelection = false;
    timeColumn.columnFilterType = ColumnFilterType.NONE;
    timeColumn.minWidth = this.oneWs;
    timeColumn.width = this.thirteenWs;
    timeColumn.resizable = true;
    timeColumn.valueGetter = (params: ValueGetterParams) => {
      return params.data.timestamp;
    };

    let timeFormatterFn = (params: ValueFormatterParams) => {
      if (CubiqTS.moment.isMoment(params.value)) {
        return params.value.format(this.localeString);
      } else if (params.value != null && params.value instanceof Date) {
        return CubiqTS.moment(params.value).format(this.localeString);
      }
    };

    timeColumn.valueFormatter = timeFormatterFn;
    timeColumn.getQuickFilterText = timeFormatterFn;
    timeColumn.position = this.position.next();
    td.addColumn(timeColumn);

    return td;
  }

  private initColdDef(): any {
    return { suppressMenu: true, cellClass: [DEFAULT_CELL_CLASS] };
  }

  public getBlotterType() {
    return BlotterType.OPSINTERVENTION;
  }

  public getAssetClass() {
    return AssetClass.FIX;
  }

  public getDefaultSortingModel(): any {
    return DEFAULT_SORT;
  }
}
